import PropTypes from "prop-types";
import { Typography, Box, Snackbar, Alert, Dialog, DialogContent, Button, TextField, CircularProgress, Avatar, Fade } from "@mui/material";
import styles from "./add.module.css";
import { Check } from "react-feather";
import { Mail, UserCheck, ArrowRight } from "react-feather";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Step1 from "./components/Step1";
import Loader from "../../../admin/components/Loader";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import Step4 from "./components/Step4";
import Step5 from "./components/Step5";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import axios from "axios";
import { useBookingCharges } from "../../../utils/bookingCharges";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import { ServiceUnavailable } from "../../../admin/components/AddonRequired";

const STEPS = ["Parcel Details", "Pickup Address", "Recipient Address", "Order Summary", "Account Details"];

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (<div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
        {value === index && (<Box className={styles.TabPanelBody}>
            <Typography component="div">{children}</Typography>
        </Box>)}
    </div>);
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function ManifestStepper({ value }) {
    return (
        <div className={styles.StepperWrap}>
            {STEPS.map((label, index) => {
                const isDone = index < value;
                const isActive = index === value;
                return (
                    <div className={styles.StepItem} key={label}>
                        <div className={styles.StepBadgeCol}>
                            <div
                                className={`${styles.StepBadge} ${isActive ? styles.StepBadgeActive : ""} ${isDone ? styles.StepBadgeDone : ""}`}
                                aria-current={isActive ? "step" : undefined}
                            >
                                {isDone ? <Check size={16} /> : String(index + 1).padStart(2, "0")}
                            </div>
                            <span className={`${styles.StepLabel} ${isActive ? styles.StepLabelActive : ""}`}>{label}</span>
                        </div>
                        {index < STEPS.length - 1 && (
                            <div className={`${styles.StepConnector} ${isDone ? styles.StepConnectorDone : ""}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/* Email-gate modal shown before the booking flow starts.
   Styled to match the brand hero (logo, primary/secondary color, rounded
   card) rather than a stock MUI dialog. Colors are passed in as props since
   this renders in a portal outside the themed wrapper div. */
function EmailGateDialog({ open, onVerified, primaryColor, secondaryColor, companyName, logoURL, utoken }) {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [checking, setChecking] = useState(false);
    const [showExistingConfirm, setShowExistingConfirm] = useState(false);

    const isValidEmail = (val) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(val);

    const handleContinue = () => {
        if (!email) {
            setError("Please enter your email");
            return;
        }
        if (!isValidEmail(email)) {
            setError("Invalid email address");
            return;
        }

        setError("");
        setChecking(true);

        // Emails are unique per company, so availability is checked against this site's company.
        axios.post(process.env.REACT_APP_API_URL + "v1/check-email", { email, utoken })
            .then((response) => {
                setChecking(false);
                if (response.data.status === "SUCCESS") {
                    if (response.data.data.is_available) {
                        onVerified(email);
                    } else {
                        setShowExistingConfirm(true);
                    }
                } else {
                    setError("Unable to verify email, please try again");
                }
            })
            .catch(() => {
                setChecking(false);
                setError("Unable to verify email, please try again");
            });
    };

    const dialogPaperSx = {
        borderRadius: "20px",
        overflow: "hidden",
        maxWidth: "420px",
        width: "100%",
    };

    return (<>
        <Dialog
            open={open && !showExistingConfirm}
            disableEscapeKeyDown
            fullWidth
            TransitionComponent={Fade}
            PaperProps={{ sx: dialogPaperSx }}
        >
            {/* Brand header strip */}
            <Box sx={{
                background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 130%)`,
                px: 4, pt: 4, pb: 6,
                position: "relative",
                textAlign: "center",
            }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.15rem", letterSpacing: "0.2px" }}>
                    Let&apos;s get your booking started
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.85rem", mt: 0.5 }}>
                    Just your email first — we&apos;ll check if you already have an account
                </Typography>
            </Box>

            {/* Floating email icon bridging header + body */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: "-28px", position: "relative", zIndex: 2 }}>
                <Avatar sx={{
                    width: 56, height: 56, bgcolor: "#fff",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                    border: `3px solid ${primaryColor}`,
                }}>
                    <Mail size={22} color={primaryColor} />
                </Avatar>
            </Box>

            <DialogContent sx={{ px: 4, pt: 3, pb: 4 }}>
                <TextField
                    label="Email address"
                    placeholder="you@example.com"
                    variant="outlined"
                    fullWidth
                    autoFocus
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleContinue(); }}
                    error={!!error}
                    helperText={error || " "}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&.Mui-focused fieldset": { borderColor: primaryColor },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: primaryColor },
                    }}
                />

                <Button
                    onClick={handleContinue}
                    disabled={checking}
                    fullWidth
                    endIcon={!checking ? <ArrowRight size={18} /> : null}
                    sx={{
                        mt: 1,
                        py: 1.3,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#fff",
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 160%)`,
                        boxShadow: `0 8px 20px -6px ${primaryColor}99`,
                        "&:hover": {
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 130%)`,
                            boxShadow: `0 10px 24px -6px ${primaryColor}cc`,
                            color: "#fff",
                        },
                        "&.Mui-disabled": { color: "#fff", opacity: 0.7 },
                    }}
                >
                    {checking ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Continue"}
                </Button>

                <Typography sx={{ textAlign: "center", fontSize: "0.75rem", color: "text.secondary", mt: 2.5 }}>
                    We&apos;ll never share your email. It&apos;s only used to check for an existing account.
                </Typography>
            </DialogContent>
        </Dialog>

        {/* Existing customer confirmation */}
        <Dialog
            open={showExistingConfirm}
            fullWidth
            TransitionComponent={Fade}
            PaperProps={{ sx: dialogPaperSx }}
        >
            <Box sx={{ px: 4, pt: 5, pb: 4, textAlign: "center" }}>
                <Avatar sx={{
                    width: 64, height: 64, mx: "auto", mb: 2.5,
                    bgcolor: `${primaryColor}1a`,
                }}>
                    <UserCheck size={28} color={primaryColor} />
                </Avatar>

                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 1 }}>
                    Welcome back!
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "0.9rem", mb: 3.5, px: 1 }}>
                    Looks like you already have an account with us. Please log in to continue with your booking.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Button
                        variant="contained"
                        disableElevation
                        fullWidth
                        onClick={() => navigate("/sign-in")}
                        sx={{
                            py: 1.2,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 160%)`,
                            boxShadow: `0 8px 20px -6px ${primaryColor}99`,
                            "&:hover": {
                                color: "#fff",
                            }
                        }}
                    >
                        Continue to login
                    </Button>
                    <Button
                        variant="text"
                        fullWidth
                        onClick={() => setShowExistingConfirm(false)}
                        sx={{
                            py: 1.1,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            color: "text.secondary",
                        }}
                    >
                        Use a different email
                    </Button>
                </Box>
            </Box>
        </Dialog>
    </>);
}

function AddBooking() {
    const [tabValue, setTabValue] = useState(0);
    const [bookingData, setBookingData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");
    const [emailGateOpen, setEmailGateOpen] = useState(true);
    const [freightGateOpen, setFreightGateOpen] = useState(false);
    const [freightType, setFreightType] = useState("");

    const { websiteData, loading: wLoading } = useWebsite();

    useSiteBranding("Add Booking", websiteData);

    const selectPCountry = websiteData?.pickup_country || 'GB';
    const selectDCountry = websiteData?.delivery_country || 'GH';

    // Collection/delivery charges come with the tenant's website config; they're worked out
    // once both addresses are known and shared by the summary (Step4) and submit (Step5) steps
    const chargeSettings = (() => {
        const raw = websiteData?.collection_delivery_charges;
        if (typeof raw !== "string") return raw || null;
        try { return JSON.parse(raw); } catch (e) { return null; }
    })();
    const chargesReady = !wLoading && !!bookingData?.pickUpData?.address && !!bookingData?.recipientData?.address;
    const chargeState = useBookingCharges(chargesReady ? chargeSettings : undefined, bookingData?.pickUpData, bookingData?.recipientData);

    const companyName = websiteData?.home_config?.companyName || "EF LOGISTICS";
    const logoURL = websiteData?.home_config?.logoURL || "/images/logo.png";
    const themePrimaryColor = websiteData?.home_config?.primaryColor || "#ef233c";
    const themeSecondaryColor = websiteData?.home_config?.secondaryColor || "#0a2a52";

    // The tenant's add-ons come with its website config; without Air Freight every booking is Sea Freight
    const airFreightOn = (websiteData?.features || []).indexOf("air_freight") > -1;

    const handleEmailVerified = (email) => {
        setEmailGateOpen(false);
        if (airFreightOn) {
            setBookingData((prev) => ({ ...prev, pickUpData: { ...(prev?.pickUpData || {}), email } }));
            setFreightGateOpen(true);
        } else {
            setFreightType("Sea Freight");
            setBookingData((prev) => ({ ...prev, freight_type: "Sea Freight", pickUpData: { ...(prev?.pickUpData || {}), email } }));
        }
    }

    const selectFreightType = (type) => {
        setFreightType(type);
        setBookingData((prev) => ({ ...prev, freight_type: type }));
        setFreightGateOpen(false);
    };

    const saveStep1Data = (data) => {
        let bookingDataTemp = bookingData;
        bookingDataTemp = { ...bookingDataTemp, ...data };

        setBookingData(bookingDataTemp);
        setTabValue((prevValue) => prevValue + 1);
    };

    const saveStep2Data = (pickUpData) => {
        let bookingDataTemp = bookingData;
        bookingDataTemp = { ...bookingDataTemp, pickUpData: pickUpData };

        setBookingData(bookingDataTemp);
        setTabValue((prevValue) => prevValue + 1);
    };

    const saveStep3Data = (recipientData) => {
        let bookingDataTemp = bookingData;
        bookingDataTemp = { ...bookingDataTemp, recipientData: recipientData };

        setBookingData(bookingDataTemp);
        setTabValue((prevValue) => prevValue + 1);
    };

    const saveStep4Data = () => {
        document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
        setTabValue((prevValue) => prevValue + 1);
    };

    const backToTab = () => {
        document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
        setTabValue((prevValue) => prevValue - 1);
    }

    // Company suspended for billing: no new bookings
    if (websiteData && websiteData?.service_active === false) {
        return <ServiceUnavailable />;
    }

    return (<>
        <EmailGateDialog
            open={emailGateOpen}
            onVerified={handleEmailVerified}
            primaryColor={themePrimaryColor}
            secondaryColor={themeSecondaryColor}
            companyName={companyName}
            logoURL={logoURL}
            utoken={websiteData?.utoken || ""}
        />

        {wLoading && <Loader />}
        <Loader open={loading} setOpen={setLoading.bind(this)} />

        <div className={`${styles.MainDivCustomer}`} style={{ "--primary-color": themePrimaryColor, "--secondary-color": themeSecondaryColor }}>
            <div className={`${styles.Container}`}>

                <div className={styles.BrandHero}>
                    <div className={styles.HeroTopBar}>
                        <Link to="/" className={styles.BackHomeBtn}>← Back to Home</Link>
                        <div className={styles.HeroChip}>New Consignment</div>
                    </div>

                    <div className={styles.BrandHeroContent}>
                        <div className={styles.BrandLeft}>
                            <div>
                                <img src={logoURL} alt={companyName} className={styles.BrandLogo} />
                            </div>

                            <div className={styles.BrandText}>
                                <h2>{companyName}</h2>
                                <p>Fast, tracked shipping from pickup to final delivery.</p>
                            </div>
                        </div>

                        <div className={styles.BrandRight}>
                            <h1>Book a Collection</h1>
                            <p>Tell us what you&apos;re sending, where it&apos;s going, and we&apos;ll take care of the rest. It only takes a few minutes.</p>
                        </div>
                    </div>
                </div>

                <div className={`${styles.MainCard}`}>

                    <div className={styles.RouteHeader}>
                        <div>
                            <p className={styles.RouteMeta}>New consignment</p>
                            <div className={styles.RoutePath}>
                                <div className={styles.RouteCode}>
                                    {selectPCountry}
                                    <span>Pickup</span>
                                </div>
                                <div className={styles.RouteLine} />
                                <div className={styles.RouteCode}>
                                    {selectDCountry}
                                    <span>Delivery</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Box sx={{ width: "100%" }} className="AddBookingTab">
                        <ManifestStepper value={tabValue} />

                        <TabPanel value={tabValue} index={0}>
                            <Step1 charges={chargeSettings} dimensionPricing={websiteData?.dimension_pricing}saveStep1Data={saveStep1Data.bind(this)} bookingData={bookingData} websiteData={websiteData} freightType={freightType} setFreightGateOpen={(open) => airFreightOn && setFreightGateOpen(open)} />
                        </TabPanel>
                        <TabPanel value={tabValue} index={1}>
                            <Step2 charges={chargeSettings} saveStep2Data={saveStep2Data.bind(this)} backToTab={backToTab.bind(this)} bookingData={bookingData} selectCountry={selectPCountry} />
                        </TabPanel>
                        <TabPanel value={tabValue} index={2}>
                            <Step3 charges={chargeSettings} saveStep3Data={saveStep3Data.bind(this)} backToTab={backToTab.bind(this)} bookingData={bookingData} selectCountry={selectDCountry} />
                        </TabPanel>
                        <TabPanel value={tabValue} index={3}>
                            <Step4 bookingData={bookingData} chargeState={chargeState} backToTab={backToTab.bind(this)} saveStep4Data={saveStep4Data.bind(this)} />
                        </TabPanel>
                        <TabPanel value={tabValue} index={4}>
                            <Step5
                                bookingData={bookingData}
                                chargeState={chargeState}
                                backToTab={backToTab.bind(this)}
                                setLoading={setLoading.bind(this)}
                                setSnackbarMessage={setSnackbarMessage.bind(this)}
                                setSnackbarType={setSnackbarType.bind(this)}
                                setSnackbarOpen={setSnackbarOpen.bind(this)}
                                websiteData={websiteData}
                            />
                        </TabPanel>
                    </Box>
                </div>
            </div>
        </div>

        <Dialog
            open={freightGateOpen}
            disableEscapeKeyDown
            fullWidth
            TransitionComponent={Fade}
            PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", maxWidth: "440px", width: "100%" } }}
        >
            <div className={styles.FreightDialogHeader}>
                <p className={styles.FreightDialogTitle}>How should this ship?</p>
                <p className={styles.FreightDialogSub}>Choose a freight option to continue your booking</p>
            </div>
            <div className={styles.FreightOptionsRow}>
                <button
                    type="button"
                    className={`${styles.FreightOptionCard} ${freightType === 'Air Freight' ? styles.FreightOptionCardActive : ''}`}
                    onClick={() => selectFreightType('Air Freight')}
                >
                    <FlightTakeoffIcon />
                    <span>Air Freight</span>
                    <small>Faster delivery, priced by weight</small>
                </button>
                <button
                    type="button"
                    className={`${styles.FreightOptionCard} ${freightType === 'Sea Freight' ? styles.FreightOptionCardActive : ''}`}
                    onClick={() => selectFreightType('Sea Freight')}
                >
                    <DirectionsBoatIcon />
                    <span>Sea Freight</span>
                    <small>Economical, priced by volume</small>
                </button>
            </div>
        </Dialog>

        <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={(e) => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <Alert
                onClose={(e) => setSnackbarOpen(false)}
                severity={snackbarType}
                variant="filled"
                sx={{ width: "100%" }}
            >
                {snackbarMessage}
            </Alert>
        </Snackbar>
    </>);
}

export default AddBooking;